import React, { Component } from 'react';
import {
  Breadcrumbs,
  IconButton,
  Tooltip,
  Skeleton,
  Button,
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Collapse,
  Radio,
  RadioGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { send_request } from '../../../utils/Request';
import { loadDraftProject } from '../../../utils/canvasApi';
import { withParams } from '../../../Routes';
import { StyledSnackbar } from '../../common/AppSnackbar';
import Email from '../../Component/Email';
import './ErrorHandling.css';

// Example JSON payload that will be sent to the webhook
const WEBHOOK_PAYLOAD_EXAMPLE = {
  event: "session_failed",
  workflow: {
    id: "<workflow_id>",
    name: "<workflow_name>"
  },
  session: {
    id: "<session_id>",
    status: "FAIL",
    startedAt: "2024-01-15T10:30:00Z",
    failedAt: "2024-01-15T10:32:15Z"
  },
  error: {
    componentId: "<component_id>",
    componentName: "<component_name>",
    message: "<error_message>"
  }
};

class ErrorHandling extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      workflowName: '',
      notifyEmails: [],
      slackWebhookUrl: '',
      webhookUrl: '',
      snackbar: { open: false, message: '', severity: 'success' },
      hasChanges: false,
      activeTab: 0,
      testingEmail: false,
      testingSlack: false,
      testingWebhook: false,
      // Error recovery settings
      autoRetryEnabled: false,
      maxRetryAttempts: 3,
      retryDelaySeconds: 5,
      retryStrategy: 'FIXED',
      maxRetryDelaySeconds: 60,
      continueOnError: false,
      // UI state
      retryExpanded: true,
      continueExpanded: true,
    };
  }

  componentDidMount() {
    this.fetchProjectData();
  }

  fetchProjectData = async () => {
    const { id } = this.props.params;
    this.setState({ loading: true });
    try {
      const response = await loadDraftProject(id);
      const project = response?.data;
      const name = project?.name || 'Untitled';
      const extras = project?.extras || {};
      
      this.setState({
        workflowName: name,
        notifyEmails: extras.notifyEmails || [],
        slackWebhookUrl: extras.slackWebhookUrl || '',
        webhookUrl: extras.webhookUrl || '',
        // Error recovery settings
        autoRetryEnabled: extras.autoRetryEnabled || false,
        maxRetryAttempts: extras.maxRetryAttempts || 3,
        retryDelaySeconds: extras.retryDelaySeconds || 5,
        retryStrategy: extras.retryStrategy || 'FIXED',
        maxRetryDelaySeconds: extras.maxRetryDelaySeconds || 60,
        continueOnError: extras.continueOnError || false,
        loading: false,
        hasChanges: false,
      });
    } catch (error) {
      console.error('Error fetching project data:', error);
      this.setState({ loading: false });
      this.showSnackbar('Failed to load error handling settings', 'error');
    }
  };

  handleSave = async () => {
    const { id } = this.props.params;
    const { 
      notifyEmails, slackWebhookUrl, webhookUrl,
      autoRetryEnabled, maxRetryAttempts, retryDelaySeconds, retryStrategy, maxRetryDelaySeconds,
      continueOnError
    } = this.state;
    
    this.setState({ saving: true });
    try {
      await send_request(
        `project-service/project/notification/${id}`,
        {
          // Auto-enable email notifications if any emails are configured
          notifyOnSessionFail: notifyEmails && notifyEmails.length > 0,
          notifyEmails,
          slackWebhookUrl,
          webhookUrl,
          // Error recovery settings
          autoRetryEnabled,
          maxRetryAttempts,
          retryDelaySeconds,
          retryStrategy,
          maxRetryDelaySeconds,
          continueOnError,
        },
        {},
        'PATCH'
      );
      this.setState({ saving: false, hasChanges: false });
      this.showSnackbar('Error handling settings saved', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      this.setState({ saving: false });
      this.showSnackbar('Failed to save settings', 'error');
    }
  };

  handleCancel = () => {
    if (this.props.isModal && this.props.onClose) {
      this.props.onClose();
    } else {
      this.fetchProjectData();
    }
  };

  handleEmailsChange = emails => {
    this.setState({ notifyEmails: emails, hasChanges: true });
  };

  handleTestEmail = async () => {
    const { id } = this.props.params;
    const { notifyEmails } = this.state;
    
    if (!notifyEmails || notifyEmails.length === 0) {
      this.showSnackbar('Please enter at least one email address', 'warning');
      return;
    }
    
    this.setState({ testingEmail: true });
    try {
      const response = await send_request(
        `project-service/project/notification/test/email/${id}`,
        { emails: notifyEmails },
        {},
        'POST'
      );
      if (response?.data?.success) {
        this.showSnackbar(response.data.message || 'Test email sent successfully', 'success');
      } else {
        this.showSnackbar(response?.data?.error || 'Failed to send test email', 'error');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      this.showSnackbar('Failed to send test email', 'error');
    }
    this.setState({ testingEmail: false });
  };

  handleTestSlack = async () => {
    const { id } = this.props.params;
    const { slackWebhookUrl } = this.state;
    
    if (!slackWebhookUrl || slackWebhookUrl.trim() === '') {
      this.showSnackbar('Please enter a Slack webhook URL', 'warning');
      return;
    }
    
    this.setState({ testingSlack: true });
    try {
      const response = await send_request(
        `project-service/project/notification/test/slack/${id}`,
        { slackWebhookUrl },
        {},
        'POST'
      );
      if (response?.data?.success) {
        this.showSnackbar(response.data.message || 'Test Slack message sent successfully', 'success');
      } else {
        this.showSnackbar(response?.data?.error || 'Failed to send test Slack message', 'error');
      }
    } catch (error) {
      console.error('Error sending test Slack message:', error);
      this.showSnackbar('Failed to send test Slack message', 'error');
    }
    this.setState({ testingSlack: false });
  };

  handleTestWebhook = async () => {
    const { id } = this.props.params;
    const { webhookUrl } = this.state;
    
    if (!webhookUrl || webhookUrl.trim() === '') {
      this.showSnackbar('Please enter a webhook URL', 'warning');
      return;
    }
    
    this.setState({ testingWebhook: true });
    try {
      const response = await send_request(
        `project-service/project/notification/test/webhook/${id}`,
        { webhookUrl },
        {},
        'POST'
      );
      if (response?.data?.success) {
        this.showSnackbar(response.data.message || 'Test webhook sent successfully', 'success');
      } else {
        this.showSnackbar(response?.data?.error || 'Failed to send test webhook', 'error');
      }
    } catch (error) {
      console.error('Error sending test webhook:', error);
      this.showSnackbar('Failed to send test webhook', 'error');
    }
    this.setState({ testingWebhook: false });
  };

  showSnackbar = (message, severity) => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  handleCloseSnackbar = () => {
    this.setState(prev => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  render() {
    const { id } = this.props.params;
    const { isModal } = this.props;
    const { 
      loading, saving, workflowName, notifyEmails, slackWebhookUrl, webhookUrl, snackbar, hasChanges, activeTab, testingEmail, testingSlack, testingWebhook,
      autoRetryEnabled, maxRetryAttempts, retryDelaySeconds, retryStrategy, maxRetryDelaySeconds, continueOnError,
      retryExpanded, continueExpanded
    } = this.state;

    return (
      <div className={`error-handling-container${isModal ? ' modal-mode' : ''}`}>
        {!isModal && (
          <Breadcrumbs separator=">" className="breadcrumbs" style={{ marginBottom: 16 }}>
            <Link className="breadcrumbItem" to="/project">
              Workflows
            </Link>
            <Link className="breadcrumbItem" to={`/project/canvas/${id}`}>
              {workflowName || 'Workflow'}
            </Link>
            <span className="breadcrumbItem">Error Handling</span>
          </Breadcrumbs>
        )}

        <div className="error-handling-header">
          {!isModal && (
            <IconButton size="small" onClick={() => this.props.navigate(`/project/canvas/${id}`)}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <h1 className="error-handling-title">
            Error Handling {!isModal && `for ${workflowName || 'Workflow'}`}
          </h1>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={this.fetchProjectData}
              disabled={loading}
              className="error-handling-refresh-btn"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>

        <div className="error-handling-info-banner">
          <InfoOutlinedIcon fontSize="small" style={{ color: '#a3a3a3', flexShrink: 0 }} />
          <p>
            Configure how this workflow handles errors during execution. When a session fails, 
            notifications can be sent via email, Slack, or a custom webhook.
          </p>
        </div>

        {loading ? (
          <div className="error-handling-content">
            <div className="error-handling-section">
              <Skeleton variant="text" width={200} height={24} style={{ marginBottom: 8 }} />
              <Skeleton variant="rectangular" width="100%" height={56} style={{ borderRadius: 4 }} />
            </div>
            <div className="error-handling-section">
              <Skeleton variant="rectangular" width={300} height={24} style={{ borderRadius: 4 }} />
            </div>
            <div className="error-handling-actions">
              <Skeleton variant="rectangular" width={80} height={36} style={{ borderRadius: 4 }} />
              <Skeleton variant="rectangular" width={80} height={36} style={{ borderRadius: 4 }} />
            </div>
          </div>
        ) : (
          <div className="error-handling-content">
            <Typography variant="subtitle2" className="error-handling-label">
              Notifications
            </Typography>
            
            <Box className="error-handling-tabs-container">
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => this.setState({ activeTab: newValue })}
                className="error-handling-tabs"
              >
                <Tab 
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Email
                      {notifyEmails && notifyEmails.length > 0 && (
                        <CheckIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                      )}
                    </span>
                  } 
                  className="error-handling-tab" 
                />
                <Tab 
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Slack
                      {slackWebhookUrl && slackWebhookUrl.trim() && (
                        <CheckIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                      )}
                    </span>
                  } 
                  className="error-handling-tab" 
                />
                <Tab 
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Webhook
                      {webhookUrl && webhookUrl.trim() && (
                        <CheckIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                      )}
                    </span>
                  } 
                  className="error-handling-tab" 
                />
              </Tabs>

              <div className="error-handling-tab-content">
                {/* Email Tab */}
                {activeTab === 0 && (
                  <div className="error-handling-tab-panel">
                    <Typography variant="body2" className="error-handling-sublabel">
                      Send notification email to
                    </Typography>
                    <div className="error-handling-email-input">
                      <Email
                        items={[]}
                        key={'error-handling-emails'}
                        defaultValue={notifyEmails}
                        placeholder="Type an email address and press Enter"
                        focus={() => {}}
                        store={this.handleEmailsChange}
                        value={notifyEmails}
                        onPlaceholderSelected={value => {
                          const newEmails = [...notifyEmails, value.key];
                          this.setState({ notifyEmails: newEmails, hasChanges: true });
                        }}
                      />
                    </div>
                    <span className="error-handling-input-helper">
                      These email addresses will receive a notification when a workflow session fails.
                    </span>
                    <div className="error-handling-test-btn-container">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handleTestEmail}
                        disabled={testingEmail || !notifyEmails || notifyEmails.length === 0}
                        className="error-handling-test-btn"
                      >
                        {testingEmail ? 'Sending...' : 'Send Test Email'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Slack Tab */}
                {activeTab === 1 && (
                  <div className="error-handling-tab-panel">
                    <Typography variant="body2" className="error-handling-sublabel">
                      Slack webhook URL
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://hooks.slack.com/services/..."
                      value={slackWebhookUrl}
                      onChange={e => this.setState({ slackWebhookUrl: e.target.value, hasChanges: true })}
                      className="error-handling-text-input"
                      InputProps={{
                        sx: { fontSize: '13px' }
                      }}
                    />
                    <span className="error-handling-input-helper">
                      Enter a Slack incoming webhook URL to receive error notifications in your Slack channel.{' '}
                      <a 
                        href="https://api.slack.com/messaging/webhooks" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="error-handling-link"
                      >
                        Learn how to create a webhook
                      </a>
                    </span>
                    <div className="error-handling-test-btn-container">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handleTestSlack}
                        disabled={testingSlack || !slackWebhookUrl || slackWebhookUrl.trim() === ''}
                        className="error-handling-test-btn"
                      >
                        {testingSlack ? 'Sending...' : 'Send Test Message'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Webhook Tab */}
                {activeTab === 2 && (
                  <div className="error-handling-tab-panel">
                    <Typography variant="body2" className="error-handling-sublabel">
                      Webhook URL (POST)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://your-server.com/webhook/error-handler"
                      value={webhookUrl}
                      onChange={e => this.setState({ webhookUrl: e.target.value, hasChanges: true })}
                      className="error-handling-text-input"
                      InputProps={{
                        sx: { fontSize: '13px' }
                      }}
                    />
                    <span className="error-handling-input-helper">
                      Enter a URL to receive a POST request with error details when a session fails.
                    </span>
                    <div className="error-handling-test-btn-container">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handleTestWebhook}
                        disabled={testingWebhook || !webhookUrl || webhookUrl.trim() === ''}
                        className="error-handling-test-btn"
                      >
                        {testingWebhook ? 'Sending...' : 'Send Test Request'}
                      </Button>
                    </div>
                    
                    <div className="error-handling-json-preview">
                      <div className="error-handling-json-label">
                        JSON Payload Preview
                      </div>
                      <pre className="error-handling-json-code">
                        {JSON.stringify(WEBHOOK_PAYLOAD_EXAMPLE, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </Box>

            {/* Error Recovery Section */}
            <Typography variant="subtitle2" className="error-handling-label" style={{ marginTop: 24 }}>
              Error Recovery
            </Typography>

            {/* Automatic Retry */}
            <Box className="error-handling-recovery-section">
              <div 
                className="error-handling-recovery-header"
                onClick={() => this.setState({ retryExpanded: !retryExpanded })}
              >
                <Typography variant="body2" className="error-handling-recovery-title">
                  Automatic Retry
                </Typography>
                {retryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={retryExpanded}>
                <div className="error-handling-recovery-content">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoRetryEnabled}
                        onChange={e => this.setState({ autoRetryEnabled: e.target.checked, hasChanges: true })}
                        size="small"
                      />
                    }
                    label={<span className="error-handling-switch-label">Enable automatic retry</span>}
                  />
                  <span className="error-handling-input-helper" style={{ marginTop: 0, marginBottom: 12 }}>
                    {continueOnError ? (
                      <>
                        Automatically retry failed components. If all retries fail, the component will be 
                        skipped and the workflow will continue (since "Continue on Error" is enabled).
                      </>
                    ) : (
                      <>
                        Automatically retry failed components before marking the session as failed.
                      </>
                    )}
                  </span>

                  {autoRetryEnabled && (
                    <div className="error-handling-retry-settings">
                      <div className="error-handling-setting-row">
                        <Typography variant="body2" className="error-handling-setting-label">
                          Max retry attempts
                        </Typography>
                        <Select
                          size="small"
                          value={maxRetryAttempts}
                          onChange={e => this.setState({ maxRetryAttempts: e.target.value, hasChanges: true })}
                          className="error-handling-select"
                        >
                          {[1, 2, 3, 4, 5].map(n => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                          ))}
                        </Select>
                      </div>

                      <div className="error-handling-setting-row">
                        <Typography variant="body2" className="error-handling-setting-label">
                          Initial retry delay
                        </Typography>
                        <Select
                          size="small"
                          value={retryDelaySeconds}
                          onChange={e => this.setState({ retryDelaySeconds: e.target.value, hasChanges: true })}
                          className="error-handling-select-wide"
                        >
                          <MenuItem value={1}>1 second</MenuItem>
                          <MenuItem value={2}>2 seconds</MenuItem>
                          <MenuItem value={5}>5 seconds</MenuItem>
                          <MenuItem value={10}>10 seconds</MenuItem>
                          <MenuItem value={15}>15 seconds</MenuItem>
                          <MenuItem value={30}>30 seconds</MenuItem>
                          <MenuItem value={60}>1 minute</MenuItem>
                          <MenuItem value={120}>2 minutes</MenuItem>
                          <MenuItem value={300}>5 minutes</MenuItem>
                          <MenuItem value={600}>10 minutes</MenuItem>
                        </Select>
                      </div>

                      <div className="error-handling-strategy-section">
                        <Typography variant="body2" className="error-handling-setting-label">
                          Retry strategy
                        </Typography>
                        <RadioGroup
                          value={retryStrategy}
                          onChange={e => this.setState({ retryStrategy: e.target.value, hasChanges: true })}
                          className="error-handling-radio-group"
                        >
                          <FormControlLabel 
                            value="FIXED" 
                            control={<Radio size="small" />} 
                            label={<span className="error-handling-radio-label">Fixed delay</span>}
                            className="error-handling-radio-option"
                          />
                          <FormControlLabel 
                            value="LINEAR" 
                            control={<Radio size="small" />} 
                            label={<span className="error-handling-radio-label">Linear backoff</span>}
                            className="error-handling-radio-option"
                          />
                          <FormControlLabel 
                            value="EXPONENTIAL" 
                            control={<Radio size="small" />} 
                            label={<span className="error-handling-radio-label">Exponential backoff</span>}
                            className="error-handling-radio-option"
                          />
                        </RadioGroup>
                        <span className="error-handling-input-helper">
                          {retryStrategy === 'FIXED' && 'Same delay between each retry attempt. Best for: internal errors, database timeouts, or when the issue is likely temporary and unrelated to load.'}
                          {retryStrategy === 'LINEAR' && 'Delay increases linearly (delay × attempt number). Best for: moderate rate limiting or when you want gradual backoff without aggressive delays.'}
                          {retryStrategy === 'EXPONENTIAL' && 'Delay doubles with each attempt (delay × 2^attempt). Best for: external API rate limits, network congestion, or services that need time to recover.'}
                        </span>
                      </div>

                      {retryStrategy !== 'FIXED' && (
                        <div className="error-handling-setting-row" style={{ marginTop: 12 }}>
                          <Typography variant="body2" className="error-handling-setting-label">
                            Max delay cap
                          </Typography>
                          <Select
                            size="small"
                            value={maxRetryDelaySeconds}
                            onChange={e => this.setState({ maxRetryDelaySeconds: e.target.value, hasChanges: true })}
                            className="error-handling-select-wide"
                          >
                            <MenuItem value={30}>30 seconds</MenuItem>
                            <MenuItem value={60}>1 minute</MenuItem>
                            <MenuItem value={120}>2 minutes</MenuItem>
                            <MenuItem value={300}>5 minutes</MenuItem>
                            <MenuItem value={600}>10 minutes</MenuItem>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Collapse>
            </Box>

            {/* Continue on Error */}
            <Box className="error-handling-recovery-section">
              <div 
                className="error-handling-recovery-header"
                onClick={() => this.setState({ continueExpanded: !continueExpanded })}
              >
                <Typography variant="body2" className="error-handling-recovery-title">
                  Continue on Error
                </Typography>
                {continueExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={continueExpanded}>
                <div className="error-handling-recovery-content">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={continueOnError}
                        onChange={e => this.setState({ continueOnError: e.target.checked, hasChanges: true })}
                        size="small"
                      />
                    }
                    label={<span className="error-handling-switch-label">Continue workflow when a component fails</span>}
                  />
                  <span className="error-handling-input-helper" style={{ marginTop: 0 }}>
                    {autoRetryEnabled ? (
                      <>
                        When enabled, if a component still fails after all retry attempts are exhausted, 
                        the workflow will skip it and continue to the next component.
                      </>
                    ) : (
                      <>
                        When enabled, the workflow will skip the failed component and continue to the next one.
                      </>
                    )}
                    {' '}The session will be marked as "Completed with errors" instead of "Failed".
                    {' '}Downstream components that depend on the skipped component's outputs may also fail 
                    due to missing placeholder values.
                  </span>
                </div>
              </Collapse>
            </Box>

            <div className="error-handling-actions">
              <Button
                variant="outlined"
                onClick={this.handleCancel}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleSave}
                disabled={saving || !hasChanges}
                size="small"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        <StyledSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={this.handleCloseSnackbar}
          autoHideDuration={4000}
        />
      </div>
    );
  }
}

export default withParams(ErrorHandling);
